import type { ChartPlan } from 'src/charting/plan/chartPlan.types'
import { compileStackedBarPlan } from 'src/charting/plan/compileStackedBarPlan'
import type { ChartDataRow, ChartSpec } from 'src/charting/spec/chartSpec.types'
import type {
  SpendChartPoint,
  SpendChartView,
} from 'src/queries/reporting/reporting.models'

import {
  firstPresentMetadataValue,
  getPointDisplayLabel,
} from './reportingChartData'
import {
  buildPeriodDomain,
  capPeriodColumns,
  detectPeriodUnit,
  formatPeriodLabelForUnit,
  ISO_REPORTING_DAY_PATTERN,
} from './reportingPeriodDomain'

const otherSeriesLabel = 'Other'

export const toReportingStackedBarPlan = ({
  chart,
  title,
  description,
  ariaLabel,
  unknownLabel,
  seriesLabelForPoint,
}: {
  chart: SpendChartView
  title: string
  description: string
  ariaLabel: string
  unknownLabel: string
  seriesLabelForPoint?: (point: SpendChartPoint) => string
}): ChartPlan => {
  const gapFilledDomain = buildPeriodDomain(chart)
  const { domain: categoryDomain, columnForBucket } = gapFilledDomain
    ? capPeriodColumns(gapFilledDomain)
    : { domain: undefined, columnForBucket: (bucket: string) => bucket }
  const periodUnit = gapFilledDomain
    ? detectPeriodUnit(
        chart.points
          .map((point) => point.bucket)
          .filter(
            (bucket): bucket is string =>
              typeof bucket === 'string' &&
              ISO_REPORTING_DAY_PATTERN.test(bucket),
          ),
      )
    : null

  const rawRows: ChartDataRow[] = chart.points.map((point) => {
    const periodLabel =
      firstPresentMetadataValue(point, ['bucketLabel', 'periodLabel']) ??
      point.bucket ??
      point.label
    const seriesLabel =
      seriesLabelForPoint?.(point) ??
      getPointDisplayLabel(point, unknownLabel, [
        'seriesLabel',
        'label',
        'name',
        'displayName',
      ])
    return {
      period: columnForBucket(periodLabel),
      series: seriesLabel,
      spendCents: point.spendCents,
      bucket: point.bucket,
    }
  })

  const topSeriesLabels = Array.from(
    rawRows
      .reduce<Map<string, number>>((acc, row) => {
        const label = String(row.series ?? unknownLabel)
        acc.set(label, (acc.get(label) ?? 0) + Number(row.spendCents ?? 0))
        return acc
      }, new Map())
      .entries(),
  )
    .filter(([label]) => label !== otherSeriesLabel)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6)
    .map(([label]) => label)

  const topSeriesLabelSet = new Set(topSeriesLabels)
  const rewrittenRows = rawRows.map((row) => {
    const label = String(row.series ?? unknownLabel)
    if (label === otherSeriesLabel || topSeriesLabelSet.has(label)) return row
    return { ...row, series: otherSeriesLabel }
  })
  const rows = Array.from(
    rewrittenRows
      .reduce<Map<string, ChartDataRow>>((acc, row) => {
        const period = String(row.period ?? '')
        const series = String(row.series ?? unknownLabel)
        const key = JSON.stringify([period, series])
        const current = acc.get(key)
        if (!current) {
          acc.set(key, row)
          return acc
        }
        acc.set(key, {
          ...current,
          spendCents:
            Number(current.spendCents ?? 0) + Number(row.spendCents ?? 0),
        })
        return acc
      }, new Map())
      .values(),
  )
  const seriesDomain = Array.from(
    new Set(rows.map((row) => String(row.series ?? unknownLabel))),
  )
  const spec: ChartSpec = {
    version: 1,
    mark: 'bar',
    title,
    description,
    ariaLabel,
    encoding: {
      x: {
        field: 'period',
        type: 'ordinal',
        title: 'Period',
        ...(categoryDomain ? { domain: categoryDomain } : {}),
      },
      y: {
        field: 'spendCents',
        type: 'quantitative',
        stack: 'diverging',
        valueFormat: 'currencyCents',
        title: 'Spend',
      },
      color: { field: 'series', type: 'nominal', domain: seriesDomain },
    },
  }
  const plan = compileStackedBarPlan(spec, rows)
  if (!periodUnit) return plan
  return {
    ...plan,
    categories: plan.categories.map((category) => ({
      ...category,
      label: formatPeriodLabelForUnit(category.key, periodUnit),
    })),
  }
}
